import { pxToRem } from '@modernhealth/vital-components';
import React, { FC, useEffect } from 'react';
import styled from '@emotion/styled';
import { ContentCollectionPlatform } from '@modernhealth/circles-graphql/v2';
import { useTranslation } from 'react-i18next';
import { Coach } from '@modernhealth/messaging-kit';
import { useSelector } from 'react-redux';
import { filterNullish } from '@modernhealth/filter-nullish';
import { captureException } from '@sentry/browser';

import { DropdownMenu, DropdownMenuItem } from 'src/shared/components/DropdownMenu';
import { InternalLinkButton } from 'src/shared/components/InternalLinkButton';
import { useProviderMessagingActionsQuery } from '../documents/getProviderMessagingActions.generated';
import { getCoachAssignmentUuid } from 'src/modules/user/selectors/userSelectors';
import { linkColorScheme, ProviderMessagingActions } from '../helpers/conversationHeaderHelpers';

const StyledBookSessionButton = styled(InternalLinkButton)({
    marginRight: pxToRem(14),
});

export interface ConversationHeaderActionsProps {
    coach: Coach;
}
export const ConversationHeaderActions: FC<ConversationHeaderActionsProps> = ({ coach }) => {
    const { t } = useTranslation();
    const assignmentUuid = useSelector(getCoachAssignmentUuid(coach.uuid));

    const { loading, error, data } = useProviderMessagingActionsQuery({
        fetchPolicy: 'network-only',
        variables: {
            platform: ContentCollectionPlatform.Web,
            assignmentUuid,
        },
    });
    useEffect(() => {
        if (error) {
            captureException(error);
        }
    }, [error]);

    const actions = (data?.providerMessagingActions ?? []).find(
        d => d?.name === ProviderMessagingActions.ACTIONS,
    );

    if (loading || !actions) {
        return null;
    }

    const [firstAction, ...otherActions] = filterNullish(actions.contentCollectionLinks || []);

    return (
        <>
            <StyledBookSessionButton
                variant="secondary"
                to={firstAction.path}
                disabled={firstAction?.disabled || false}
            >
                {firstAction.title}
            </StyledBookSessionButton>

            {otherActions.length > 0 && (
                <DropdownMenu
                    buttonTitle={t('messaging:conversation.header.more_provider_actions')}
                    popperAriaLabel={t('messaging:conversation.header.more_provider_actions.menu')}
                >
                    {otherActions.map(link => (
                        <DropdownMenuItem
                            key={link.titleTranslationKey}
                            title={link.title}
                            linkTo={link.path}
                            color={linkColorScheme[link.colorScheme ?? '']}
                        />
                    ))}
                </DropdownMenu>
            )}
        </>
    );
};
